import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import TikTokIcon from "@/assets/tiktok";
import { DashboardCard } from "@/components/dashboard/dashboard-card";
import { cn } from "@/lib/utils";
import {
  getAffiliateTikTokStanding,
  type AffiliateTikTokStanding,
  type AffiliateTikTokWindow,
} from "@/services/tiktokShop.service";

type Slot = "7D" | "30D";
const SLOT_LABEL: Record<Slot, string> = {
  "7D": "Last 7 days",
  "30D": "Last 30 days",
};

/**
 * TikTok sends the band either as an enum ("GMV_RANGE_1000_10000") or as
 * money ("$27100.00~$42400.00"); both become "$1K–$10K" style text.
 */
function gmvBand(range: string): string {
  const k = (n: number) =>
    n >= 1000
      ? `$${(n / 1000).toLocaleString("en-US", { maximumFractionDigits: 1 })}K`
      : `$${Math.round(n)}`;
  const enumMatch = /^GMV_RANGE_(\d+)(?:_(\d+)|_AND_ABOVE)$/.exec(range);
  if (enumMatch) {
    return enumMatch[2]
      ? `${k(Number(enumMatch[1]))}–${k(Number(enumMatch[2]))}`
      : `${k(Number(enumMatch[1]))}+`;
  }
  const moneyMatch = /^\$?([\d.,]+)\s*[~–-]\s*\$?([\d.,]+)$/.exec(range.trim());
  if (moneyMatch) {
    const [lo, hi] = [moneyMatch[1], moneyMatch[2]].map((v) =>
      Number(v.replace(/,/g, "")),
    );
    if (Number.isFinite(lo) && Number.isFinite(hi)) return `${k(lo)}–${k(hi)}`;
  }
  return range;
}

/**
 * The affiliate's own TikTok Shop standing with this brand: their place on
 * the brand's bestselling-creators list and the sales band TikTok discloses.
 * Exact per-order TikTok figures arrive with the affiliate order sync; until
 * then this is what TikTok lets a shop see about its creators. Renders
 * nothing when the brand has no TikTok Shop connected.
 */
export function TikTokCard({ programId }: { programId: string }) {
  const [slot, setSlot] = useState<Slot>("30D");
  const query = useQuery({
    queryKey: ["affiliate-tiktok-standing", programId],
    queryFn: () => getAffiliateTikTokStanding(programId),
    enabled: true,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const standing = query.data;
  // A 404 means the brand is outside the TikTok pre-release: show nothing.
  if (
    query.isError ||
    !standing ||
    standing.reason === "BRAND_NOT_CONNECTED"
  ) {
    return null;
  }

  const window = standing.windows?.[slot] ?? null;
  return (
    <DashboardCard
      id="dashboard-tiktok-card"
      title="TikTok Shop"
      action={
        standing.available ? (
          <div className="flex items-center gap-2">
            <TikTokIcon className="size-4 text-muted-foreground" />
            <div className="flex rounded-md border border-border p-0.5 text-xs">
              {(["7D", "30D"] as Slot[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSlot(s)}
                  className={cn(
                    "rounded px-2 py-0.5",
                    s === slot
                      ? "bg-muted font-medium text-foreground"
                      : "text-muted-foreground",
                  )}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <TikTokIcon className="size-4 text-muted-foreground" />
        )
      }
      bodyClassName="px-5 pb-5"
    >
      <Body standing={standing} window={window} slot={slot} />
    </DashboardCard>
  );
}

function Body({
  standing,
  window,
  slot,
}: {
  standing: AffiliateTikTokStanding;
  window: AffiliateTikTokWindow | null;
  slot: Slot;
}) {
  const shop = standing.shopName
    ? `${standing.shopName} on TikTok Shop`
    : "this brand's TikTok Shop";
  if (standing.reason === "NO_HANDLE") {
    return (
      <p className="text-sm text-muted-foreground">
        {standing.shopName ?? "This brand"} sells on TikTok Shop.{" "}
        <Link
          to="/affiliate/settings"
          className="font-medium text-brand-700 underline-offset-4 hover:underline"
        >
          Add your TikTok handle
        </Link>{" "}
        to see your TikTok sales for them here.
      </p>
    );
  }
  if (!standing.available) {
    return (
      <p className="text-sm text-muted-foreground">
        TikTok's figures for {shop} aren't available right now.
      </p>
    );
  }
  if (!window) {
    return (
      <p className="text-sm text-muted-foreground">
        No TikTok Shop sales for {shop} under @{standing.handles[0]} in the{" "}
        {SLOT_LABEL[slot].toLowerCase()}.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-baseline gap-2 leading-none">
        <span className="text-4xl font-semibold tabular-nums tracking-tight text-foreground">
          {gmvBand(window.gmvRange)}
        </span>
        <span className="text-sm text-muted-foreground">
          in TikTok sales, {SLOT_LABEL[slot].toLowerCase()}
        </span>
      </div>
      <p className="text-sm text-muted-foreground">
        #{window.rank} of {standing.shopName ?? "the brand"}'s TikTok creators
        as @{standing.handles[0]}. TikTok shows sales as a range and pays this
        commission to you directly, so it isn't part of your Reveshare payouts.
      </p>
      {window.topVideos.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {window.topVideos.map((v) => (
            <a
              key={v.id}
              href={v.url}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
              title={v.title}
            >
              {v.title.length > 40
                ? `${v.title.slice(0, 40)}…`
                : v.title || "Video"}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
